
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BulkActionResult {
  gameId: string;
  gameTitle: string;
  success: boolean;
  error?: string;
}

interface ProgressCallback {
  onProgress?: (processedGames: number, results: BulkActionResult[]) => void;
}

export const useAdminGamesMutations = () => {
  const queryClient = useQueryClient();

  // Delete game mutation
  const deleteGameMutation = useMutation({
    mutationFn: async (gameId: string) => {
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('id', gameId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      toast.success('Game deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete game: ' + error.message);
    },
  });

  // Bulk delete mutation with progress tracking
  const bulkDeleteMutation = useMutation({
    mutationFn: async ({ gameIds, onProgress }: { gameIds: string[]; onProgress?: (processedGames: number, results: BulkActionResult[]) => void }) => {
      const results: BulkActionResult[] = [];
      
      // First, get game titles for progress display
      const { data: games } = await supabase
        .from('games')
        .select('id, title')
        .in('id', gameIds);
      
      const gameMap = games?.reduce((acc, game) => {
        acc[game.id] = game.title;
        return acc;
      }, {} as Record<string, string>) || {};

      for (let i = 0; i < gameIds.length; i++) {
        const gameId = gameIds[i];
        const gameTitle = gameMap[gameId] || 'Unknown';
        
        try {
          const { error } = await supabase
            .from('games')
            .delete()
            .eq('id', gameId);
          
          if (error) throw error;
          
          results.push({
            gameId,
            gameTitle,
            success: true
          });
        } catch (error) {
          results.push({
            gameId,
            gameTitle,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
        
        onProgress?.(i + 1, [...results]);
      }
      
      return results;
    },
    onSuccess: () => {
      // Only invalidate queries after all operations are complete
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      toast.success('Bulk delete completed');
    },
    onError: (error) => {
      toast.error('Bulk delete failed: ' + error.message);
    },
  });

  // Toggle visibility mutation
  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ gameId, hidden }: { gameId: string; hidden: boolean }) => {
      const { error } = await supabase
        .from('games')
        .update({ hidden })
        .eq('id', gameId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      toast.success('Game visibility updated');
    },
    onError: (error) => {
      toast.error('Failed to update visibility: ' + error.message);
    },
  });

  // Enhanced bulk update mutation with progress tracking and no intermediate invalidations
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ 
      gameIds, 
      updates, 
      useFakePlayRange, 
      bulkFakePlayMin, 
      bulkFakePlayMax,
      onProgress
    }: { 
      gameIds: string[]; 
      updates: any;
      useFakePlayRange?: boolean;
      bulkFakePlayMin?: string;
      bulkFakePlayMax?: string;
      onProgress?: (processedGames: number, results: BulkActionResult[]) => void;
    }) => {
      const results: BulkActionResult[] = [];
      
      // First, get game titles for progress display
      const { data: games } = await supabase
        .from('games')
        .select('id, title')
        .in('id', gameIds);
      
      const gameMap = games?.reduce((acc, game) => {
        acc[game.id] = game.title;
        return acc;
      }, {} as Record<string, string>) || {};

      if ('fake_play_count' in updates && useFakePlayRange && bulkFakePlayMin && bulkFakePlayMax) {
        const minCount = parseInt(bulkFakePlayMin);
        const maxCount = parseInt(bulkFakePlayMax);
        
        for (let i = 0; i < gameIds.length; i++) {
          const gameId = gameIds[i];
          const gameTitle = gameMap[gameId] || 'Unknown';
          
          try {
            const randomCount = Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount;
            const { error } = await supabase
              .from('games')
              .update({ fake_play_count: randomCount })
              .eq('id', gameId);
            
            if (error) throw error;
            
            results.push({
              gameId,
              gameTitle,
              success: true
            });
          } catch (error) {
            results.push({
              gameId,
              gameTitle,
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
          
          onProgress?.(i + 1, [...results]);
        }
      } else if ('rating' in updates && updates.rating === 'random_4_5') {
        const minRatings = parseInt(updates.minRatingsCount);
        const maxRatings = parseInt(updates.maxRatingsCount);
        
        for (let i = 0; i < gameIds.length; i++) {
          const gameId = gameIds[i];
          const gameTitle = gameMap[gameId] || 'Unknown';
          
          try {
            let randomRating = parseFloat((Math.random() + 4).toFixed(1));
            if (randomRating > 5.0) randomRating = 5.0;
            const randomRatingsCount = Math.floor(Math.random() * (maxRatings - minRatings + 1)) + minRatings;

            const { error } = await supabase
              .from('games')
              .update({ rating: randomRating, ratings_count: randomRatingsCount })
              .eq('id', gameId);
            
            if (error) throw error;
            
            results.push({
              gameId,
              gameTitle,
              success: true
            });
          } catch (error) {
            results.push({
              gameId,
              gameTitle,
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
          
          onProgress?.(i + 1, [...results]);
        }
      } else {
        // Process in smaller batches for better progress tracking
        const batchSize = 10;
        for (let i = 0; i < gameIds.length; i += batchSize) {
          const batch = gameIds.slice(i, i + batchSize);
          
          try {
            const { error } = await supabase
              .from('games')
              .update(updates)
              .in('id', batch);
            
            if (error) throw error;
            
            batch.forEach(gameId => {
              results.push({
                gameId,
                gameTitle: gameMap[gameId] || 'Unknown',
                success: true
              });
            });
          } catch (error) {
            batch.forEach(gameId => {
              results.push({
                gameId,
                gameTitle: gameMap[gameId] || 'Unknown',
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
              });
            });
          }
          
          onProgress?.(Math.min(i + batchSize, gameIds.length), [...results]);
        }
      }
      
      return results;
    },
    onSuccess: () => {
      // Only invalidate queries after all operations are complete
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      toast.success('Bulk update completed');
    },
    onError: (error) => {
      toast.error('Bulk update failed: ' + error.message);
    },
  });

  return {
    deleteGameMutation,
    bulkDeleteMutation,
    toggleVisibilityMutation,
    bulkUpdateMutation
  };
};
